import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function DoctorPatients({ doctor }) {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    // Records View State
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [records, setRecords] = useState([]);
    const [newNote, setNewNote] = useState("");
    const [uploadFile, setUploadFile] = useState(null);

    // Vitals State
    const [isEditingVitals, setIsEditingVitals] = useState(false);
    const [vitalsData, setVitalsData] = useState({
        height: "", weight: "", blood_group: "", blood_pressure: ""
    });
    // Unit states for doctor edits
    const [vhVal, setVhVal] = useState("");
    const [vhUnit, setVhUnit] = useState("cm");
    const [vwVal, setVwVal] = useState("");
    const [vwUnit, setVwUnit] = useState("kg");

    const fetchPatients = async () => {
        try {
            const res = await fetch(`${API}/api/doctor-portal/${doctor.id}/patients`);
            const data = await res.json();
            setPatients(data);
            setLoading(false);
        } catch (err) {
            toast.error("Failed to load patients");
            setLoading(false);
        }
    };

    useEffect(() => {
        if (doctor?.id) fetchPatients();
    }, [doctor]);

    // --- Filtered patients based on search query ---
    const filteredPatients = patients.filter((p) => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return true;
        const nameMatch = p.name?.toLowerCase().includes(q);
        const idMatch = String(p.id).includes(q);
        return nameMatch || idMatch;
    });

    const handleViewRecords = async (patient) => {
        setSelectedPatient(patient);
        setIsEditingVitals(false);
        setVitalsData({
            height: patient.height || "",
            weight: patient.weight || "",
            blood_group: patient.blood_group || "",
            blood_pressure: patient.blood_pressure || ""
        });

        if (patient.height) {
            const hStr = String(patient.height);
            const match = hStr.match(/^([\d.]+)\s*(.*)$/);
            if (match) { setVhVal(match[1]); setVhUnit(match[2] || "cm"); }
            else { setVhVal(hStr); setVhUnit("cm"); }
        } else { setVhVal(""); setVhUnit("cm"); }

        if (patient.weight) {
            const wStr = String(patient.weight);
            const match = wStr.match(/^([\d.]+)\s*(.*)$/);
            if (match) { setVwVal(match[1]); setVwUnit(match[2] || "kg"); }
            else { setVwVal(wStr); setVwUnit("kg"); }
        } else { setVwVal(""); setVwUnit("kg"); }

        try {
            const res = await fetch(`${API}/api/doctor-portal/${doctor.id}/records/${patient.id}`);
            const data = await res.json();
            setRecords(data);
        } catch (err) {
            toast.error("Failed to fetch records");
        }
    };

    const handleUpdateVitals = async () => {
        const updatedVitals = {
            ...vitalsData,
            height: vhVal ? `${vhVal} ${vhUnit}` : "",
            weight: vwVal ? `${vwVal} ${vwUnit}` : ""
        };

        try {
            const res = await fetch(`${API}/api/users/profile/${selectedPatient.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updatedVitals)
            });

            if (res.ok) {
                toast.success("Vitals updated successfully!");
                setIsEditingVitals(false);
                setVitalsData(updatedVitals);
                fetchPatients();
            } else {
                toast.error("Failed to update vitals");
            }
        } catch (err) {
            toast.error("Server error");
        }
    };

    const handleAddRecord = async (e) => {
        e.preventDefault();
        if (!newNote && !uploadFile) return toast.error("Please add a note or upload a file.");

        const fd = new FormData();
        if (newNote) fd.append("notes", newNote);
        if (uploadFile) fd.append("document", uploadFile);

        try {
            const res = await fetch(`${API}/api/doctor-portal/${doctor.id}/records/${selectedPatient.id}`, {
                method: "POST",
                body: fd
            });

            if (res.ok) {
                toast.success("Record added successfully!");
                setNewNote("");
                setUploadFile(null);
                handleViewRecords(selectedPatient);
            } else {
                toast.error("Failed to add record");
            }
        } catch (err) {
            toast.error("Server error");
        }
    };

    // ─── Patient Detail View ───────────────────────────────────────────────
    if (selectedPatient) {
        return (
            <div className="max-w-5xl mx-auto px-4 md:px-8 relative z-10 w-full animate-in fade-in slide-in-from-right-4 duration-300">
                {/* Back button */}
                <button
                    onClick={() => setSelectedPatient(null)}
                    className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-800 font-semibold mb-6 bg-white/70 backdrop-blur-sm px-5 py-2.5 rounded-xl border border-sky-100 shadow-sm transition-all hover:shadow-md hover:-translate-x-0.5"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Patients
                </button>

                <div className="bg-white/85 backdrop-blur-md rounded-2xl shadow-xl border border-white/80 overflow-hidden">
                    {/* Patient Header */}
                    <div className="bg-gradient-to-r from-sky-600 to-blue-700 p-6 md:p-8">
                        <div className="flex items-center gap-5">
                            <div className="w-18 h-18 w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white font-black text-2xl shadow-inner border border-white/30">
                                {selectedPatient.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1">
                                <h2 className="text-2xl font-extrabold text-white tracking-tight">{selectedPatient.name}</h2>
                                <p className="text-sky-100 text-sm mt-1 flex items-center gap-3 flex-wrap">
                                    <span className="flex items-center gap-1">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                        {selectedPatient.email}
                                    </span>
                                    {selectedPatient.phone && (
                                        <span className="flex items-center gap-1">
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                            {selectedPatient.phone}
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div className="hidden md:flex flex-col items-end gap-1">
                                <span className="text-[11px] text-sky-200 uppercase tracking-widest font-semibold">Patient ID</span>
                                <span className="text-white font-mono font-bold text-lg">#{selectedPatient.id}</span>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 md:p-8 space-y-8">
                        {/* Vitals Card */}
                        <div className="bg-gradient-to-br from-sky-50 to-blue-50/50 rounded-2xl p-6 border border-sky-100 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-sky-500 to-blue-600 rounded-l-2xl"></div>
                            <div className="flex justify-between items-center mb-5">
                                <h3 className="text-base font-bold text-sky-900 flex items-center gap-2">
                                    <span className="text-lg">🩺</span> Patient Vitals
                                </h3>
                                {!isEditingVitals ? (
                                    <button
                                        onClick={() => setIsEditingVitals(true)}
                                        className="text-xs font-bold uppercase tracking-wider text-sky-600 hover:text-white hover:bg-sky-600 bg-white px-4 py-1.5 rounded-lg border border-sky-200 shadow-sm transition-all"
                                    >
                                        Edit
                                    </button>
                                ) : (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleUpdateVitals}
                                            className="text-xs font-bold uppercase tracking-wider text-white bg-sky-600 hover:bg-sky-700 px-4 py-1.5 rounded-lg shadow-sm transition-all"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={() => setIsEditingVitals(false)}
                                            className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700 bg-white px-4 py-1.5 rounded-lg border border-slate-200 transition-all"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {/* Height */}
                                <div className="bg-white/80 p-4 rounded-xl border border-white shadow-sm flex flex-col gap-2">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Height</span>
                                    {!isEditingVitals ? (
                                        <span className="text-xl font-black text-slate-700">{vitalsData.height || "—"}</span>
                                    ) : (
                                        <div className="flex gap-1">
                                            <input type="number" value={vhVal} onChange={(e) => setVhVal(e.target.value)}
                                                className="w-full text-sm bg-white border border-sky-100 rounded-lg p-1.5 focus:ring-2 focus:ring-sky-200 outline-none" />
                                            <select value={vhUnit} onChange={(e) => setVhUnit(e.target.value)}
                                                className="text-[10px] bg-white border border-sky-100 rounded-lg px-1 outline-none">
                                                <option value="cm">cm</option><option value="in">in</option><option value="ft">ft</option>
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {/* Weight */}
                                <div className="bg-white/80 p-4 rounded-xl border border-white shadow-sm flex flex-col gap-2">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Weight</span>
                                    {!isEditingVitals ? (
                                        <span className="text-xl font-black text-slate-700">{vitalsData.weight || "—"}</span>
                                    ) : (
                                        <div className="flex gap-1">
                                            <input type="number" value={vwVal} onChange={(e) => setVwVal(e.target.value)}
                                                className="w-full text-sm bg-white border border-sky-100 rounded-lg p-1.5 focus:ring-2 focus:ring-sky-200 outline-none" />
                                            <select value={vwUnit} onChange={(e) => setVwUnit(e.target.value)}
                                                className="text-[10px] bg-white border border-sky-100 rounded-lg px-1 outline-none">
                                                <option value="kg">kg</option><option value="lbs">lbs</option>
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {/* Blood Group */}
                                <div className="bg-white/80 p-4 rounded-xl border border-white shadow-sm flex flex-col gap-2">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Blood Group</span>
                                    {!isEditingVitals ? (
                                        <span className="text-xl font-black text-slate-700">{vitalsData.blood_group || "—"}</span>
                                    ) : (
                                        <select
                                            value={vitalsData.blood_group}
                                            onChange={(e) => setVitalsData({ ...vitalsData, blood_group: e.target.value })}
                                            className="text-sm bg-white border border-sky-100 rounded-lg p-1.5 outline-none"
                                        >
                                            <option value="">Select</option>
                                            {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(g => <option key={g} value={g}>{g}</option>)}
                                        </select>
                                    )}
                                </div>

                                {/* Blood Pressure */}
                                <div className="bg-white/80 p-4 rounded-xl border border-white shadow-sm flex flex-col gap-2">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Blood Pressure</span>
                                    {!isEditingVitals ? (
                                        <span className="text-xl font-black text-slate-700">{vitalsData.blood_pressure || "—"}</span>
                                    ) : (
                                        <input
                                            type="text"
                                            value={vitalsData.blood_pressure}
                                            onChange={(e) => setVitalsData({ ...vitalsData, blood_pressure: e.target.value })}
                                            placeholder="120/80"
                                            className="text-sm bg-white border border-sky-100 rounded-lg p-1.5 focus:ring-2 focus:ring-sky-200 outline-none"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Records Section */}
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Add Record Form */}
                            <div className="bg-slate-50/80 rounded-2xl p-6 border border-slate-100">
                                <h3 className="text-base font-bold text-slate-700 mb-5 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-slate-800 rounded-md flex items-center justify-center">
                                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                        </svg>
                                    </span>
                                    Add Medical Record
                                </h3>
                                <form onSubmit={handleAddRecord} className="space-y-4">
                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">Doctor's Notes</label>
                                        <textarea
                                            className="w-full border border-slate-200 rounded-xl p-3 text-sm text-slate-700 focus:border-sky-400 focus:ring-4 focus:ring-sky-400/10 outline-none transition-all placeholder-slate-300 resize-none bg-white"
                                            rows="4"
                                            placeholder="Enter clinical observations, diagnoses, prescriptions..."
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                        ></textarea>
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">Attach Document <span className="text-slate-300 normal-case font-normal">(optional)</span></label>
                                        <input
                                            type="file"
                                            className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100 transition-colors cursor-pointer"
                                            onChange={(e) => setUploadFile(e.target.files[0])}
                                        />
                                        <p className="text-[11px] text-slate-400 mt-1.5">Max 5 MB · PDF, JPG, PNG</p>
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-white font-semibold py-3 rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 text-sm mt-2 flex items-center justify-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                                        Save Record
                                    </button>
                                </form>
                            </div>

                            {/* Past Records */}
                            <div>
                                <h3 className="text-base font-bold text-slate-700 mb-5 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-sky-600 rounded-md flex items-center justify-center">
                                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </span>
                                    Past Records
                                    {records.length > 0 && (
                                        <span className="ml-auto text-[11px] font-bold text-sky-600 bg-sky-50 px-2.5 py-1 rounded-full border border-sky-100">
                                            {records.length} {records.length === 1 ? "entry" : "entries"}
                                        </span>
                                    )}
                                </h3>
                                {records.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-10 text-center bg-slate-50/80 rounded-2xl border border-slate-100">
                                        <svg className="w-10 h-10 text-slate-200 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                        <p className="text-slate-400 text-sm font-medium">No records found</p>
                                        <p className="text-slate-300 text-xs mt-1">Add a record using the form on the left.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3 pr-1 max-h-[480px] overflow-y-auto custom-scrollbar">
                                        {records.map(rec => (
                                            <div key={rec.id} className="bg-white border border-slate-100 shadow-sm p-4 rounded-xl hover:shadow-md hover:border-sky-100 transition-all duration-200">
                                                <div className="flex justify-between items-center mb-2.5">
                                                    <span className="text-[11px] font-bold text-sky-600 bg-sky-50 px-2.5 py-1 rounded-md border border-sky-100">
                                                        {new Date(rec.created_at).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })}
                                                    </span>
                                                </div>
                                                {rec.notes && <p className="text-[13px] text-slate-600 mt-2 whitespace-pre-wrap leading-relaxed">{rec.notes}</p>}
                                                {(rec.recorded_height || rec.recorded_weight || rec.recorded_blood_pressure || rec.recorded_blood_group) && (
                                                    <div className="mt-3 grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
                                                        {rec.recorded_height && <div className="text-[11px] font-medium text-slate-400 uppercase tracking-tight">H: <span className="text-slate-600 font-bold">{rec.recorded_height}</span></div>}
                                                        {rec.recorded_weight && <div className="text-[11px] font-medium text-slate-400 uppercase tracking-tight">W: <span className="text-slate-600 font-bold">{rec.recorded_weight}</span></div>}
                                                        {rec.recorded_blood_pressure && <div className="text-[11px] font-medium text-slate-400 uppercase tracking-tight">BP: <span className="text-slate-600 font-bold">{rec.recorded_blood_pressure}</span></div>}
                                                        {rec.recorded_blood_group && <div className="text-[11px] font-medium text-slate-400 uppercase tracking-tight">BG: <span className="text-slate-600 font-bold">{rec.recorded_blood_group}</span></div>}
                                                    </div>
                                                )}
                                                {rec.document_url && (
                                                    <a
                                                        href={`${API}${rec.document_url}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-2 mt-3 text-[12px] font-semibold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg transition-colors border border-emerald-100"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                                                        View Attachment
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <ToastContainer />
            </div>
        );
    }

    // ─── Patients List View ────────────────────────────────────────────────
    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 space-y-6 relative z-10 w-full animate-in fade-in duration-500">

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-blue-700 tracking-tight">
                        My Patients
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">Manage records and health data for your registered patients.</p>
                </div>
                {!loading && patients.length > 0 && (
                    <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2.5 rounded-xl border border-slate-100 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                        <span className="text-sm font-semibold text-slate-600">{patients.length} total patient{patients.length !== 1 ? "s" : ""}</span>
                    </div>
                )}
            </div>

            {/* Search Bar */}
            {!loading && patients.length > 0 && (
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="w-4.5 h-4.5 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by patient name or ID…"
                        className="w-full pl-12 pr-12 py-3.5 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-400/10 shadow-sm transition-all"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery("")}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            )}

            {/* Patients Grid */}
            <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/60">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-4">
                        <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-sky-100 border-t-sky-500"></div>
                        <p className="text-slate-400 text-sm font-medium">Loading patients…</p>
                    </div>
                ) : patients.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                        <p className="text-slate-600 font-semibold">No patients yet</p>
                        <p className="text-slate-400 text-sm mt-1">Patients who have booked with you will appear here.</p>
                    </div>
                ) : filteredPatients.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mb-4">
                            <svg className="w-7 h-7 text-sky-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <p className="text-slate-600 font-semibold">No results found</p>
                        <p className="text-slate-400 text-sm mt-1">No patients match "<span className="font-medium text-slate-500">{searchQuery}</span>"</p>
                        <button
                            onClick={() => setSearchQuery("")}
                            className="mt-4 text-sm font-semibold text-sky-600 hover:text-sky-700 underline underline-offset-2"
                        >
                            Clear search
                        </button>
                    </div>
                ) : (
                    <>
                        {searchQuery && (
                            <p className="text-[12px] text-slate-400 font-medium mb-4">
                                Showing <span className="font-bold text-slate-600">{filteredPatients.length}</span> result{filteredPatients.length !== 1 ? "s" : ""} for "{searchQuery}"
                            </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filteredPatients.map(p => (
                                <div
                                    key={p.id}
                                    className="group bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:-translate-y-1.5 hover:border-sky-100 transition-all duration-300 cursor-default"
                                >
                                    {/* Card Header */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white font-black text-lg shadow-md group-hover:scale-105 transition-transform">
                                            {p.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-slate-800 text-[15px] leading-tight truncate">{p.name}</h3>
                                            <p className="text-[11px] text-slate-400 mt-0.5">{p.city || "No City"}</p>
                                        </div>
                                        <span className="text-[10px] font-mono font-bold text-slate-300 bg-slate-50 px-2 py-1 rounded-md border border-slate-100 flex-shrink-0">
                                            #{p.id}
                                        </span>
                                    </div>

                                    {/* Contact Info */}
                                    <div className="space-y-2 mb-4 text-[12px] text-slate-500 border-t border-slate-50 pt-4">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <svg className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            <span className="truncate">{p.email}</span>
                                        </div>
                                        {p.phone && (
                                            <div className="flex items-center gap-2">
                                                <svg className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                                <span>{p.phone}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Vitals Preview */}
                                    {(p.blood_group || p.blood_pressure) && (
                                        <div className="flex gap-2 mb-4 flex-wrap">
                                            {p.blood_group && (
                                                <span className="text-[10px] font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-md border border-red-100">
                                                    🩸 {p.blood_group}
                                                </span>
                                            )}
                                            {p.blood_pressure && (
                                                <span className="text-[10px] font-bold text-purple-500 bg-purple-50 px-2 py-0.5 rounded-md border border-purple-100">
                                                    💓 {p.blood_pressure}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Action Button */}
                                    <button
                                        onClick={() => handleViewRecords(p)}
                                        className="w-full bg-gradient-to-r from-sky-50 to-sky-100 text-sky-700 hover:from-sky-600 hover:to-blue-600 hover:text-white font-semibold py-2.5 border border-sky-200 hover:border-transparent rounded-xl transition-all shadow-sm hover:shadow-md text-[13px] flex items-center justify-center gap-2 group/btn"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        View Records
                                    </button>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
            <ToastContainer />
        </div>
    );
}
